/**
 * Error Logger - Captures logs and errors for bug reporting
 *
 * Usage:
 * - Automatically captures console.log, warn, error
 * - Call errorLogger.captureError(error) for manual errors
 * - Call errorLogger.getBugReport() to get all logs as string
 * - Call errorLogger.saveBugReport() to save to Firebase
 */

import { storage } from './storage';

export interface LogEntry {
  timestamp: string;
  level: 'log' | 'warn' | 'error' | 'info';
  message: string;
  data?: any;
  stack?: string;
}

export interface BugReport {
  id: string;
  userId: string;
  timestamp: string;
  userAgent: string;
  url: string;
  screenSize: string;
  appVersion: string;
  buildId?: string;
  logs: LogEntry[];
  userDescription?: string;
  programId?: string;
  currentWeek?: number;
  currentDay?: number;
}

const MAX_LOGS = 100;
const STORAGE_KEY = 'errorLogs';
const AUTO_SAVE_DEBOUNCE_MS = 10000; // Don't auto-save more than once per 10 seconds
const AUTO_SAVE_KEY = 'lastAutoSavedBug';
const AUTO_SAVE_COUNT_KEY = 'autoSaveCount';
const MAX_AUTO_SAVES_PER_SESSION = 5;
// Resolved at runtime from public/version.json (written by the prebuild step) so
// every report identifies the exact build it came from. Falls back to 'unknown'
// rather than a hardcoded literal — a stale version string makes triage impossible.
let APP_VERSION = 'unknown';
let BUILD_ID: string | undefined;

async function resolveAppVersion(): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return;
    const v = await res.json();
    if (typeof v?.version === 'string') APP_VERSION = v.version;
    if (typeof v?.buildId === 'string') BUILD_ID = v.buildId;
  } catch {
    // Offline or the file is missing — keep 'unknown'. Never block logging on this.
  }
}

const IS_PRODUCTION = typeof window !== 'undefined' && window.location.hostname !== 'localhost';

class ErrorLogger {
  private logs: LogEntry[] = [];
  private isInitialized = false;
  private autoSaveTimeout: NodeJS.Timeout | null = null;
  private pendingAutoSave = false;

  /**
   * Initialize the error logger - call this once at app start
   */
  init() {
    if (this.isInitialized || typeof window === 'undefined') return;

    // Fire-and-forget: stamps APP_VERSION/BUILD_ID before the first report is filed
    void resolveAppVersion();

    // Load existing logs from localStorage
    this.loadLogs();
    
    // Override console methods to capture logs
    const originalConsole = {
      log: console.log.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      info: console.info.bind(console),
    };

    console.log = (...args: any[]) => {
      this.addLog('log', args);
      originalConsole.log(...args);
    };

    console.warn = (...args: any[]) => {
      this.addLog('warn', args);
      originalConsole.warn(...args);
    };

    console.error = (...args: any[]) => {
      this.addLog('error', args);
      originalConsole.error(...args);
    };

    console.info = (...args: any[]) => {
      this.addLog('info', args);
      originalConsole.info(...args);
    };

    // CAPTURE-phase error filter — runs BEFORE Next.js's error listener.
    // Cross-origin script errors (Stripe, Firebase iframe, third-party scripts)
    // produce ErrorEvents with `e.error === null` due to browser security.
    // Next.js's framework error handler tries to call `.includes()` on these
    // null errors and crashes with "e.includes is undefined", which breaks
    // the entire error overlay system. Stop those events at the capture phase
    // so Next.js never sees them.
    window.addEventListener('error', (event) => {
      // Cross-origin script error: error is null, message is "Script error."
      if (event.error === null || event.error === undefined) {
        const isScriptError = event.message === 'Script error.' || !event.filename;
        if (isScriptError) {
          // Silent — third party script error we can't do anything about
          event.stopImmediatePropagation();
          event.preventDefault();
          return;
        }
      }
    }, true); // capture phase = true, runs before bubble listeners

    // Same for unhandled rejections from cross-origin scripts
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      // Filter out null/undefined rejections and cross-origin script errors
      if (reason === null || reason === undefined) {
        event.stopImmediatePropagation();
        event.preventDefault();
        return;
      }
      // Filter Firefox extension webauthn errors
      if (typeof reason === 'object' && reason?.message?.includes?.('Permission denied to access property')) {
        event.stopImmediatePropagation();
        event.preventDefault();
        return;
      }
    }, true);

    // Catch unhandled errors (bubble phase, for our own logging)
    window.onerror = (message, source, lineno, colno, error) => {
      // Skip cross-origin script errors
      if (!error && message === 'Script error.') return false;
      this.captureError(error || new Error(String(message)), {
        source,
        lineno,
        colno,
      });
      this.scheduleAutoSave();
      return false;
    };

    // Catch unhandled promise rejections
    window.onunhandledrejection = (event) => {
      if (event.reason === null || event.reason === undefined) return;
      this.captureError(event.reason, { type: 'unhandledRejection' });
      this.scheduleAutoSave();
    };

    this.isInitialized = true;
    console.log('Error logger initialized');
  }

  /**
   * Schedule auto-save of bug report (debounced to avoid spam)
   */
  private scheduleAutoSave() {
    // Don't auto-save if already pending
    if (this.pendingAutoSave) return;

    if (typeof window !== 'undefined') {
      // Cap auto-saves per session
      const count = parseInt(sessionStorage.getItem(AUTO_SAVE_COUNT_KEY) || '0', 10);
      if (count >= MAX_AUTO_SAVES_PER_SESSION) return;

      // Check if we've auto-saved recently
      const lastSave = storage.get(AUTO_SAVE_KEY);
      if (lastSave) {
        const timeSinceLastSave = Date.now() - parseInt(lastSave, 10);
        if (timeSinceLastSave < AUTO_SAVE_DEBOUNCE_MS) {
          return; // Skip, saved too recently
        }
      }
    }

    this.pendingAutoSave = true;

    // Debounce: wait a bit to batch multiple errors together
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
    }

    this.autoSaveTimeout = setTimeout(async () => {
      try {
        await this.saveAutoReport();
        if (typeof window !== 'undefined') {
          storage.set(AUTO_SAVE_KEY, Date.now().toString());
          const count = parseInt(sessionStorage.getItem(AUTO_SAVE_COUNT_KEY) || '0', 10);
          sessionStorage.setItem(AUTO_SAVE_COUNT_KEY, String(count + 1));
        }
      } catch (e) {
        console.error('Auto-save bug report failed:', e);
      } finally {
        this.pendingAutoSave = false;
        this.autoSaveTimeout = null;
      }
    }, 2000); // Wait 2 seconds to batch errors
  }

  /**
   * Save auto-logged bug report (no user description)
   */
  private async saveAutoReport(): Promise<void> {
    // Only save if there are actual errors in the logs
    const hasErrors = this.logs.some(l => l.level === 'error');
    if (!hasErrors) return;

    try {
      const { db, auth } = await import('./firebase');
      const { doc, setDoc, Timestamp } = await import('firebase/firestore');

      const report = this.generateBugReport(); // No user description = auto report

      const reportRef = doc(db, 'bug_reports', report.id);

      // Build the document data, filtering out undefined values
      const docData: Record<string, any> = {
        id: report.id,
        userId: auth.currentUser?.uid || report.userId || 'unknown',
        timestamp: Timestamp.fromDate(new Date(report.timestamp)),
        userAgent: report.userAgent,
        url: report.url,
        screenSize: report.screenSize,
        logs: report.logs.slice(-50),
        appVersion: APP_VERSION,
        ...(BUILD_ID ? { buildId: BUILD_ID } : {}),
        status: 'new',
        isAutoLogged: true,
      };

      if (auth.currentUser?.email) docData.userEmail = auth.currentUser.email;
      if (report.programId) docData.programId = report.programId;
      if (report.currentWeek !== undefined) docData.currentWeek = report.currentWeek;
      if (report.currentDay !== undefined) docData.currentDay = report.currentDay;

      await setDoc(reportRef, docData);

      console.log('Auto-logged bug report saved:', report.id);
    } catch (error) {
      // Silently fail - don't want to cause more errors
      console.error('Failed to auto-save bug report:', error);
    }
  }

  /**
   * Add a log entry
   */
  private addLog(level: LogEntry['level'], args: any[]) {
    // In production, skip capturing verbose debug logs to save memory
    if (IS_PRODUCTION && level === 'log') {
      const msg = String(args[0] || '');
      // Only capture logs that start with emoji indicators or key prefixes
      if (!msg.match(/^[^\x00-\x7F]/) && !msg.startsWith('Error') && !msg.startsWith('Failed')) {
        return;
      }
    }

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message: args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg, null, 2);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' '),
    };

    this.logs.push(entry);
    
    // Keep only last MAX_LOGS
    if (this.logs.length > MAX_LOGS) {
      this.logs = this.logs.slice(-MAX_LOGS);
    }

    // Persist to localStorage (debounced)
    this.saveLogs();
  }

  /**
   * Capture an error with stack trace
   */
  captureError(error: Error | any, context?: Record<string, any>) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: 'error',
      message: error?.message || String(error),
      stack: error?.stack,
      data: context,
    };

    this.logs.push(entry);
    this.saveLogs();
  }

  /**
   * Save logs to localStorage
   */
  private saveLogs() {
    if (typeof window === 'undefined') return;
    try {
      storage.set(STORAGE_KEY, JSON.stringify(this.logs));
    } catch (e) {
      // localStorage might be full or unavailable
    }
  }

  /**
   * Load logs from localStorage
   */
  private loadLogs() {
    if (typeof window === 'undefined') return;
    try {
      const stored = storage.get(STORAGE_KEY);
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch (e) {
      this.logs = [];
    }
  }

  /**
   * Get all captured logs
   */
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * Clear all logs
   */
  clearLogs() {
    this.logs = [];
    if (typeof window !== 'undefined') {
      storage.remove(STORAGE_KEY);
    }
  }

  /**
   * Generate a bug report with all relevant info
   */
  generateBugReport(userDescription?: string): BugReport {
    // Get user/program context
    let userId = 'unknown';
    let programId: string | undefined;
    let currentWeek: number | undefined;
    let currentDay: number | undefined;

    if (typeof window !== 'undefined') {
      try {
        const state = storage.get('tacticalFitnessState');
        if (state) {
          const parsed = JSON.parse(state);
          userId = parsed.userId || userId;
        }

        const programSelection = storage.get('userProgramSelection');
        if (programSelection) {
          const parsed = JSON.parse(programSelection);
          programId = parsed.programId;
          currentWeek = parsed.currentWeek;
          currentDay = parsed.currentDay;
        }
      } catch (e) {
        // Ignore
      }
    }

    return {
      id: `bug_${Date.now()}`,
      userId,
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      screenSize: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : 'unknown',
      appVersion: APP_VERSION,
      ...(BUILD_ID ? { buildId: BUILD_ID } : {}),
      logs: this.getLogs(),
      userDescription,
      programId,
      currentWeek,
      currentDay,
    };
  }

  /**
   * Get bug report as formatted string (for copying)
   */
  getBugReportString(userDescription?: string): string {
    const report = this.generateBugReport(userDescription);
    
    let output = `=== BUG REPORT ===\n`;
    output += `ID: ${report.id}\n`;
    output += `Version: ${report.appVersion}\n`;
    output += `Time: ${report.timestamp}\n`;
    output += `URL: ${report.url}\n`;
    output += `Screen: ${report.screenSize}\n`;
    output += `User Agent: ${report.userAgent}\n`;
    output += `Program: ${report.programId || 'N/A'}\n`;
    output += `Week/Day: ${report.currentWeek || '?'}/${report.currentDay || '?'}\n`;
    
    if (userDescription) {
      output += `\n=== USER DESCRIPTION ===\n${userDescription}\n`;
    }

    output += `\n=== LOGS (${report.logs.length} entries) ===\n`;
    
    // Show errors first
    const errors = report.logs.filter(l => l.level === 'error');
    if (errors.length > 0) {
      output += `\n--- ERRORS (${errors.length}) ---\n`;
      errors.forEach(log => {
        output += `[${log.timestamp}] ${log.message}\n`;
        if (log.stack) {
          output += `Stack: ${log.stack}\n`;
        }
        if (log.data) {
          output += `Context: ${JSON.stringify(log.data)}\n`;
        }
        output += '\n';
      });
    }

    // Show warnings
    const warnings = report.logs.filter(l => l.level === 'warn');
    if (warnings.length > 0) {
      output += `\n--- WARNINGS (${warnings.length}) ---\n`;
      warnings.slice(-20).forEach(log => {
        output += `[${log.timestamp}] ${log.message}\n`;
      });
    }

    // Show last 30 logs
    output += `\n--- RECENT LOGS (last 30) ---\n`;
    report.logs.slice(-30).forEach(log => {
      const icon = log.level === 'error' ? '' : log.level === 'warn' ? '' : 'ℹ';
      output += `${icon} [${log.timestamp.split('T')[1].split('.')[0]}] ${log.message.substring(0, 200)}\n`;
    });

    return output;
  }

  /**
   * Save bug report to Firebase (with PWA/offline fallback)
   */
  async saveBugReport(userDescription?: string): Promise<string> {
    const report = this.generateBugReport(userDescription);
    console.log('saveBugReport: Generated report with ID:', report.id);
    console.log('saveBugReport: User description:', userDescription ? userDescription.substring(0, 50) + '...' : '(none)');

    try {
      console.log('saveBugReport: Importing Firebase...');
      const { db, auth } = await import('./firebase');
      const { doc, setDoc, Timestamp } = await import('firebase/firestore');

      console.log('saveBugReport: Firebase imported. Auth user:', auth.currentUser?.uid || 'not authenticated');

      // Use bug_reports collection (snake_case) which allows anonymous creation
      // The bugReports (camelCase) collection requires authentication
      const reportRef = doc(db, 'bug_reports', report.id);
      console.log('saveBugReport: Writing to Firestore...');

      // Build the document data, filtering out undefined values (Firestore doesn't accept them)
      const docData: Record<string, any> = {
        id: report.id,
        userId: auth.currentUser?.uid || report.userId || 'unknown',
        timestamp: Timestamp.fromDate(new Date(report.timestamp)),
        userAgent: report.userAgent,
        url: report.url,
        screenSize: report.screenSize,
        logs: report.logs.slice(-50), // Limit to last 50 logs for storage
        appVersion: APP_VERSION,
        ...(BUILD_ID ? { buildId: BUILD_ID } : {}),
        status: 'new', // For admin review
      };

      // Only add optional fields if they have values
      if (auth.currentUser?.email) docData.userEmail = auth.currentUser.email;
      if (report.userDescription) docData.userDescription = report.userDescription;
      if (report.programId) docData.programId = report.programId;
      if (report.currentWeek !== undefined) docData.currentWeek = report.currentWeek;
      if (report.currentDay !== undefined) docData.currentDay = report.currentDay;

      await setDoc(reportRef, docData);

      console.log('saveBugReport: SUCCESS - Bug report saved to Firebase:', report.id);
      return report.id;
    } catch (error) {
      console.error('saveBugReport: FAILED to save to Firebase:', error);

      // PWA/Offline fallback: Save to localStorage for later sync
      try {
        const pendingReports = JSON.parse(storage.get('pendingBugReports') || '[]');
        pendingReports.push({
          ...report,
          status: 'pending_sync',
          failedAt: new Date().toISOString(),
        });
        storage.set('pendingBugReports', JSON.stringify(pendingReports));
        console.log('saveBugReport: FALLBACK - Bug report saved to localStorage for later sync:', report.id);

        // Still return success so user gets positive feedback
        return report.id;
      } catch (localError) {
        console.error('saveBugReport: FAILED to save to localStorage:', localError);
        throw error; // Throw original Firebase error
      }
    }
  }

  /**
   * Sync pending bug reports (call when online)
   */
  async syncPendingBugReports(): Promise<number> {
    if (typeof window === 'undefined') return 0;
    
    try {
      const pendingReports = JSON.parse(storage.get('pendingBugReports') || '[]');
      if (pendingReports.length === 0) return 0;

      const { db } = await import('./firebase');
      const { doc, setDoc, Timestamp } = await import('firebase/firestore');
      
      let synced = 0;
      const stillPending = [];

      for (const report of pendingReports) {
        try {
          const reportRef = doc(db, 'bug_reports', report.id);
          await setDoc(reportRef, {
            ...report,
            timestamp: Timestamp.fromDate(new Date(report.timestamp)),
            status: 'synced_from_offline',
          });
          synced++;
          console.log('Synced pending bug report:', report.id);
        } catch (e) {
          // Keep in pending if sync fails
          stillPending.push(report);
        }
      }

      // Update localStorage with remaining pending reports
      storage.set('pendingBugReports', JSON.stringify(stillPending));
      
      if (synced > 0) {
        console.log(`Synced ${synced} pending bug reports`);
      }
      
      return synced;
    } catch (error) {
      console.error('Failed to sync pending bug reports:', error);
      return 0;
    }
  }
}

// Singleton instance
export const errorLogger = new ErrorLogger();

// Helper to copy bug report to clipboard
export async function copyBugReportToClipboard(userDescription?: string): Promise<boolean> {
  try {
    const report = errorLogger.getBugReportString(userDescription);
    await navigator.clipboard.writeText(report);
    return true;
  } catch (e) {
    console.error('Failed to copy bug report:', e);
    return false;
  }
}

